import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import { InvalidateGPTProjectQueries } from "./useCreateProject";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";

type DeleteGPTViewChat = { type: "view"; chatID: string; viewID: string };
type DeleteGPTRecordChat = {
  type: "record";
  chatID: string;
  recordID: string;
};

export type DeleteGPTChatProps = DeleteGPTViewChat | DeleteGPTRecordChat;

// Used to a delete cloud - uses non permanent delete endpoint currently
// On successful delete, the user is redirected to the cloud switcher page.
const useDeleteGPTChat = (
  projectID: string | undefined | null,
  tab: BrieGPTTabs
) => {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom) || "";

  const deleteGPTChat = useMutation({
    mutationFn: (props: DeleteGPTChatProps) => {
      return BrieConversationAPI.deleteBrieGPTChat(props);
    },
    onSuccess: (_, props) => {
      let queryIds = props.type === "view" ? [props.viewID] : [props.recordID];

      queryClient.removeQueries({
        queryKey: [tenantID, ...queryIds, props.chatID, "BrieGPTMessages"],
      });

      if (projectID) {
        InvalidateGPTProjectQueries({
          tab,
          tenantID,
          itemID: queryIds[0],
          client: queryClient,
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [tenantID, ...queryIds, "BrieGPTChats"],
        });
      }

      if (params.get("chatID") === props.chatID) {
        params.delete("chatID");
        setParams(params);
      }

      newToastEvent("success", "Chat successfully deleted.");
    },
    onError: () => {
      newToastEvent("error", "Failed to delete chat.");
    },
  });

  return {
    deleteChat: deleteGPTChat.mutate,
    deletingChat: deleteGPTChat.isPending,
    deleteChatError: deleteGPTChat.error
      ? getErrorMessage(deleteGPTChat.error)
      : null,
  };
};

export default useDeleteGPTChat;
